import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpGet } from '../../HttpRequestHandling/httpRequestHandler';
import { formatNumber } from '../../components/Common/Utilities';
import { saveQueue as saveInvoicesTab } from '../lists/invoiceList';
import { insights, FETCH_WEEKS, QUEUE_LABELS, bucketUnit, periodName } from './insightsModel';
import { ColumnChart, BarList, Legend, Sparkline, StackBar } from './charts';

// The dashboard's Insights view (docs/ui_redesign/16-vendor-mapping-and-dashboard-concepts.md, "Insights", option A):
// KPI tiles and charts from calls the app already makes. Nothing here changes data; every number that leads somewhere
// opens the Work view's queue, the Invoices page or the Payment dashboard.

const C = { received: '#94A3B8', posted: 'var(--apw-accent)', ok: 'var(--apw-ok)', warn: '#D97706', bad: 'var(--apw-bad)', ink: 'var(--apw-ink-700)', mut: '#CBD5E1', scan: '#64748B', partial: '#FBBF24', unpaid: '#E2E8F0' };
const QUEUE_COLORS = { scan: C.scan, unmatched: C.warn, correct: C.warn, approve: C.posted, ready: C.ok, failed: C.bad };
const EXC_COLORS = { vendor: '#D97706', missing: '#F59E0B', errors: '#FB923C', amount: '#FCD34D', rejected: '#E11D48', failed: 'var(--apw-bad)' };
const compact = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 2 });
const listOf = d => (Array.isArray(d) ? d : Array.isArray(d && d.listOfItems) ? d.listOfItems : []);
const money = parts => parts.map(([c, a]) => `${compact.format(a)} ${c}`).join(' · ');

function Delta({ change: c, compare }) {
	if (!compare || c == null) return null;
	const up = c >= 0;
	return <span className={'apw2-delta ' + (up ? 'up' : 'dn')}>{up ? '▲' : '▼'} {Math.abs(c)}%</span>;
}

function Card({ span = 4, title, sub, source, link, onLink, children, id }) {
	return (
		<section className={'apw2-ins-card s' + span} aria-labelledby={id}>
			<div className="apw2-ins-ch">
				<h3 id={id}>{title}</h3>
				{link && <button type="button" className="apw2-ins-link" onClick={onLink}>{link} ›</button>}
			</div>
			<div className="apw2-ins-cs">{sub}{source && <span className="apw2-ins-src">{source}</span>}</div>
			{children}
		</section>
	);
}

export default function Insights({ modelDefId, base, weeks, compare, queued, pending, onOpenQueue }) {
	const navigate = useNavigate();
	const fetchWeeks = FETCH_WEEKS[weeks] != null ? FETCH_WEEKS[weeks] : 0;
	const [invoices, setInvoices] = useState(null);
	const [payments, setPayments] = useState(null);
	const [failed, setFailed] = useState({});
	const [currency, setCurrency] = useState(null);

	useEffect(() => {
		let live = true;
		setInvoices(null);
		httpGet(`/api/invoicedocument/${modelDefId}?searchFilter=&weeksToFilter=${fetchWeeks}`)
			.then(r => { if (live) setInvoices(listOf(r.data)); }, () => { if (live) { setInvoices([]); setFailed(f => ({ ...f, invoices: true })); } });
		return () => { live = false; };
	}, [modelDefId, fetchWeeks]);
	useEffect(() => {
		let live = true;
		httpGet(`/api/dashboard/appayments/${modelDefId}`)
			.then(r => { if (live) setPayments(listOf(r.data)); }, () => { if (live) { setPayments([]); setFailed(f => ({ ...f, payments: true })); } });
		return () => { live = false; };
	}, [modelDefId]);

	const r = useMemo(() => insights({ invoices: invoices || [], payments: payments || [], queued, pending, weeks }), [invoices, payments, queued, pending, weeks]);
	const loading = invoices == null || payments == null;
	const toInvoices = tab => { if (tab) saveInvoicesTab(modelDefId, tab); navigate(`${base}/ManageInvoiceDocuments/`); };
	const toPayments = () => navigate(`${base}/PaymentDashboard/`);
	const vendorsIn = r.topVendors.find(v => v.currency === currency) || r.topVendors[0];
	const payAmounts = r.payments.parts.map(p => ({ ...p, amount: (p.amounts.find(([c]) => c === (vendorsIn && vendorsIn.currency)) || [null, 0])[1] }));
	const prev = compare ? ` vs previous ${weeks === 1 ? 'week' : weeks + ' weeks'}` : '';

	if (loading) return <div className="apw2-ins-loading" role="status">Loading insights…</div>;

	return (
		<div className="apw2-ins" aria-label="Insights">
			{(failed.invoices || failed.payments) && (
				<div className="apw2-ins-warn" role="status">
					{failed.invoices ? 'The Invoices list could not be loaded. ' : ''}{failed.payments ? 'The Payment dashboard could not be loaded. ' : ''}The numbers that need it show as 0.
				</div>
			)}
			<div className="apw2-kpis" role="list" aria-label="Key figures">
				<div className="apw2-kpi" role="listitem"><div className="k">Invoices received</div><div className="v">{r.received.count}</div>
					<div className="d"><Delta change={r.received.change} compare={compare} />{compare && r.received.change != null ? prev : `${periodName(weeks).toLowerCase()}`}</div>
					<Sparkline values={r.received.series} color={C.received} label="Received per period" /></div>
				<div className="apw2-kpi" role="listitem"><div className="k">Posted</div><div className="v">{r.posted.count}</div>
					<div className="d">{r.posted.amounts.length ? money(r.posted.amounts) : 'Nothing posted'}{compare && r.posted.change != null ? <> · <Delta change={r.posted.change} compare /></> : null}</div>
					<Sparkline values={r.posted.series} color={C.posted} label="Posted per period" /></div>
				<div className="apw2-kpi" role="listitem"><div className="k">Open now</div><div className="v">{r.open.count}</div>
					<div className="d">{r.open.needFix ? <><b className="warn">{r.open.needFix}</b> need a fix</> : 'Nothing to fix'}</div></div>
				<div className="apw2-kpi" role="listitem"><div className="k">Past due, not posted</div><div className="v">{r.pastDue}</div>
					<div className="d">{r.dueSoon} more due in 7 days</div></div>
				<div className="apw2-kpi" role="listitem"><div className="k">Oldest open</div><div className="v">{r.oldest != null ? r.oldest : '–'}{r.oldest != null && <small> days</small>}</div>
					<div className="d">{r.over14} open over 14 days</div></div>
				<div className="apw2-kpi" role="listitem"><div className="k">Paid in full</div><div className="v">{r.payments.paidPct != null ? r.payments.paidPct : '–'}{r.payments.paidPct != null && <small>%</small>}</div>
					<div className="d">of the {r.payments.count} in the Payment dashboard</div></div>
			</div>

			<div className="apw2-ins-grid">
				<Card span={8} id="ins-week" title="Received and posted" sub={`${periodName(weeks)}, by ${bucketUnit(weeks).toLowerCase().replace(' starting', '')}`} source="Invoices list" link="Invoices" onLink={() => toInvoices('all')}>
					<Legend items={[{ label: 'Received', color: C.received }, { label: 'Posted', color: C.posted }]} />
					<ColumnChart labels={r.buckets.map(b => b.label)} unit={bucketUnit(weeks)} label={`Invoices received and posted, ${periodName(weeks).toLowerCase()}`}
						series={[{ key: 'r', label: 'Received', color: C.received, values: r.received.series }, { key: 'p', label: 'Posted', color: C.posted, values: r.posted.series }]} />
				</Card>
				<Card span={4} id="ins-queue" title="Open invoices by queue" sub={`${r.open.count} open`} source="Invoices list" link="Work view" onLink={() => onOpenQueue(null)}>
					<BarList label="Open invoices by queue" rows={['scan', 'unmatched', 'correct', 'approve', 'ready', 'failed'].map(k => ({ key: k, label: QUEUE_LABELS[k], value: r.open.queues[k] || 0, color: QUEUE_COLORS[k], onClick: () => onOpenQueue(k), title: `Open ${QUEUE_LABELS[k]}` }))} />
				</Card>
				<Card span={4} id="ins-age" title="Age of open invoices" sub="since received" source="Invoices list" link="Invoices" onLink={() => toInvoices('all')}>
					<ColumnChart height={200} width={360} labels={r.aging.map(a => a.label)} label="Open invoices by age" series={[{ key: 'a', label: 'Open', color: C.posted, values: r.aging.map(a => a.count) }]} />
				</Card>
				<Card span={4} id="ins-exc" title="Exceptions by reason" sub={`${r.open.needFix} open now`} source="Invoices list">
					<BarList label="Exceptions by reason" rows={[...r.exceptions].sort((a, b) => b.count - a.count).map(e => ({
						key: e.key, label: e.label, value: e.count, color: EXC_COLORS[e.key],
						onClick: () => (e.open.invoices ? toInvoices(e.open.invoices) : onOpenQueue(e.open.queue, e.open.chip || null)),
						title: e.open.invoices ? 'Open the Invoices page (the dashboard queues don’t list this status)' : `Open ${QUEUE_LABELS[e.open.queue]}`
					}))} />
				</Card>
				<Card span={4} id="ins-appr" title="Waiting for approval" sub={`${r.open.queues.approve || 0} invoices`} source="Invoices list · Pending approval queue" link="Pending approval" onLink={() => onOpenQueue('approve')}>
					{r.levels.length ? <BarList label="Waiting by approval level" rows={r.levels.map(([l, n]) => ({ key: 'l' + l, label: `Level ${l}`, value: n, color: l % 2 ? C.posted : C.ink }))} /> : <p className="apw2-ins-none">Nothing waiting for approval.</p>}
					{r.approvers.length > 0 && <>
						<div className="apw2-ins-sub">Most assigned</div>
						<BarList label="Most assigned approvers" rows={r.approvers.map(([n, v]) => ({ key: n, label: n, value: v, color: C.mut }))} />
					</>}
				</Card>
				<Card span={6} id="ins-vend" title="Top vendors by posted amount" sub={vendorsIn ? `Posted invoices, total amount in ${vendorsIn.currency}, ${periodName(weeks).toLowerCase()}` : periodName(weeks)} source="Invoices list" link="Posted invoices" onLink={() => toInvoices('posted')}>
					{r.topVendors.length > 1 && (
						<div className="apw2-ins-cur" role="group" aria-label="Currency">
							{r.topVendors.map(v => <button type="button" key={v.currency} aria-pressed={v === vendorsIn} className={v === vendorsIn ? 'on' : ''} onClick={() => setCurrency(v.currency)}>{v.currency}</button>)}
						</div>
					)}
					{vendorsIn ? <BarList label="Top vendors by posted amount" rows={vendorsIn.rows.map(([n, a]) => ({ key: n, label: n, value: a, text: formatNumber(a), color: C.posted }))} /> : <p className="apw2-ins-none">Nothing posted in this period.</p>}
				</Card>
				<Card span={6} id="ins-pay" title="Payment of posted invoices" sub={`${r.payments.count} posted, ${periodName(weeks).toLowerCase()}`} source="Payment dashboard" link="Payment dashboard" onLink={toPayments}>
					<StackBar label="Posted invoices by payment status" parts={[
						{ key: 'full', label: 'Fully paid', value: r.payments.parts[0].count, color: C.ok },
						{ key: 'partial', label: 'Partially paid', value: r.payments.parts[1].count, color: C.partial },
						{ key: 'unpaid', label: 'Unpaid', value: r.payments.parts[2].count, color: C.unpaid }]} />
					<div className="apw2-legend">
						{[['Fully paid', C.ok], ['Partially paid', C.partial], ['Unpaid', C.unpaid]].map(([l, c], i) => (
							<span key={l}><i style={{ background: c }} />{l} <b>{payAmounts[i].count}</b>{vendorsIn && payAmounts[i].amount ? ` · ${formatNumber(payAmounts[i].amount)} ${vendorsIn.currency}` : ''}</span>
						))}
					</div>
				</Card>
			</div>
			<p className="apw2-ins-note">
				From the Invoices list ({fetchWeeks ? `invoices created or changed in the last ${fetchWeeks} weeks` : 'all invoices'}; a posted invoice counts in the period of its last change), the Payment dashboard (payment status) and the Work view’s queues (waiting to scan, approvers). Amounts are per currency.
			</p>
		</div>
	);
}
